/** Multi-step onboarding modal guiding new users through a sample extraction demo. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client/react';
import { noop } from 'es-toolkit';
import { Button, Icon, Loader, Modal } from '@insio/ui-toolkit';
import { useTranslation } from 'react-i18next';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { navigation } from '@/navigation';
import { documentUploadService } from '@/features/documents/services/document-upload.service';
import { TappeerAvatarE } from './TappeerAvatarE';
import { OrganizationStep } from './steps/OrganizationStep';
import { AccountTypeStep } from './steps/AccountTypeStep';
import { useAccountTypeStep } from './steps/use-account-type-step';

/** Returns the correct sample invoice PDF URL based on locale. */
function getSampleInvoiceUrl(locale: string): string {
  const filename = locale.startsWith('cs') ? 'ukazkova-faktura.pdf' : 'sample-invoice.pdf';
  return `/onboarding/${filename}`;
}

/** Minimum duration of the fake extraction animation in ms */
const MIN_PROCESSING_DURATION = 8000;

/** How long the loader shows before auto-redirect (ms) */
const REDIRECT_DELAY = 2000;

type OnboardingStep =
  'welcome' | 'organization' | 'accountType' | 'preview' | 'processing' | 'complete';

export interface OnboardingModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Called when onboarding flow is completed (user navigates to invoice) */
  onComplete: () => void;
  /** Called when user skips onboarding entirely */
  onSkip: () => void;
}

/**
 * Fetch the sample invoice PDF from the public directory and return it as a File.
 */
async function fetchSampleInvoice(url: string): Promise<File> {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to fetch sample invoice: ${response.status} ${response.statusText}`);
  }
  const blob = await response.blob();
  const filename = url.split('/').pop() ?? 'sample-invoice.pdf';
  return new File([blob], filename, { type: 'application/pdf' });
}

/**
 * Onboarding modal that guides users through a fake extraction demo. Declining the demo on the
 * welcome step still runs the setup steps; every later step can be left by its close button.
 */
export function OnboardingModal({ open, onComplete, onSkip }: OnboardingModalProps) {
  const t = useFeatureTranslation('onboarding');
  const accountType = useAccountTypeStep(open);
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const sampleInvoiceUrl = getSampleInvoiceUrl(i18n.language);
  const apolloClient = useApolloClient();
  const [step, setStep] = useState<OnboardingStep>('welcome');
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  // Whether the user asked for the sample invoice demo, or only for the setup steps.
  const [withSample, setWithSample] = useState(false);
  // The modal stays mounted after `open` flips false, so in-flight work must know it was left.
  const skippedRef = useRef(false);

  // Reset step when modal opens
  useEffect(() => {
    if (open) {
      skippedRef.current = false;
      setStep('welcome');
      setWithSample(false);
      setInvoiceId(null);
      setPreviewBlobUrl(null);
    }
  }, [open]);

  /** Leaves onboarding and stops any step work that is still running. */
  const handleSkip = useCallback(() => {
    skippedRef.current = true;
    onSkip();
  }, [onSkip]);

  // Fetch PDF blob URL when entering preview step to avoid Content-Disposition: attachment
  useEffect(() => {
    if (step !== 'preview') {
      // Reset state while component is still mounted (safe setState).
      setPreviewBlobUrl(null);
      return;
    }
    let cancelled = false;
    let createdUrl: string | null = null;
    fetchSampleInvoice(sampleInvoiceUrl)
      .then(file => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(file);
        setPreviewBlobUrl(createdUrl);
      })
      .catch(() => {
        // Fall back to direct URL if fetch fails
        if (!cancelled) setPreviewBlobUrl(sampleInvoiceUrl);
      });
    return () => {
      cancelled = true;
      // Revoke without calling setState — avoids state updates during unmount.
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [step, sampleInvoiceUrl]);

  // Auto-redirect when complete step is reached
  useEffect(() => {
    if (!open || step !== 'complete') return;

    const timer = setTimeout(() => {
      onComplete();
      apolloClient.refetchQueries({ include: ['Documents'] }).catch(() => {});
      if (invoiceId) {
        navigate(navigation.invoiceDetail(invoiceId));
      } else {
        navigate(navigation.invoices());
      }
    }, REDIRECT_DELAY);

    return () => clearTimeout(timer);
  }, [open, step, invoiceId, onComplete, apolloClient, navigate]);

  // Start upload when entering processing step
  const handleStartProcessing = useCallback(async () => {
    const timerPromise = new Promise<void>(resolve => setTimeout(resolve, MIN_PROCESSING_DURATION));

    try {
      const file = await fetchSampleInvoice(sampleInvoiceUrl);

      const uploadResult = await documentUploadService.uploadDocument({
        file,
        documentType: 'INVOICE',
        skipExtraction: true,
        locale: i18n.language
      });

      if (uploadResult.invoiceId) {
        setInvoiceId(uploadResult.invoiceId);
      }
      apolloClient.refetchQueries({ include: ['Documents'] }).catch(() => {});

      await timerPromise;
      if (skippedRef.current) return;
      setStep('complete');
    } catch {
      await timerPromise;
      if (skippedRef.current) return;
      // Edge case: upload failed — skip onboarding gracefully
      handleSkip();
    }
  }, [apolloClient, handleSkip, sampleInvoiceUrl, i18n.language]);

  // Kick off upload when processing step is reached
  useEffect(() => {
    if (!open || step !== 'processing') return;
    handleStartProcessing();
  }, [open, step, handleStartProcessing]);

  const handleTrySample = useCallback(() => {
    setWithSample(true);
    setStep('organization');
  }, []);

  /** "I know Tappeer" runs the setup steps but leaves out the sample invoice demo. */
  const handleKnowTappeer = useCallback(() => {
    setWithSample(false);
    setStep('organization');
  }, []);

  /** After the setup steps: on to the sample invoice, or done when the user declined it. */
  const continueAfterSetup = useCallback(() => {
    if (withSample) setStep('preview');
    else handleSkip();
  }, [withSample, handleSkip]);

  // Skipped up front when already decided, so the step never flashes; otherwise it waits on a loader.
  const handleOrganizationDone = useCallback(() => {
    if (accountType.phase === 'skip') continueAfterSetup();
    else setStep('accountType');
  }, [accountType.phase, continueAfterSetup]);

  // The people resolved to someone else (or could not be read) while the step was waiting;
  // a confirmed declaration pins the phase, so a refusal's reload never moves on from here.
  useEffect(() => {
    if (step === 'accountType' && accountType.phase === 'skip') continueAfterSetup();
  }, [step, accountType.phase, continueAfterSetup]);

  return (
    <Modal
      open={open}
      onClose={noop}
      size='sm'
      layout='vertical'
      className={
        step === 'preview'
          ? 'w-full! sm:w-200! transition-all duration-300'
          : 'w-full! sm:w-150! transition-all duration-300'
      }
    >
      {step === 'welcome' && (
        <>
          <Modal.Header onClose={handleKnowTappeer}>
            <span className='text-lg font-medium'>{t('title')}</span>
          </Modal.Header>

          <Modal.Content className='flex flex-col items-center gap-lg p-lg sm:p-xl text-center'>
            <TappeerAvatarE className='w-32 h-32' />

            <div className='flex flex-col gap-sm'>
              <h2 className='text-xl font-semibold text-foreground-base'>{t('subtitle')}</h2>
              <p className='text-sm text-foreground-muted'>{t('description')}</p>
            </div>
          </Modal.Content>

          <Modal.Footer className='flex-col! items-stretch!'>
            <Modal.Action>
              <Button
                variant='primary'
                size='md'
                onClick={handleTrySample}
                className='w-full bg-black! hover:bg-foreground-base! text-white!'
              >
                <Icon name='bolt' className='mr-xs' />
                {t('trySample')}
              </Button>
            </Modal.Action>
            <Button
              variant='ghost'
              size='sm'
              onClick={handleKnowTappeer}
              className='w-full text-sm text-foreground-muted hover:text-foreground-base'
            >
              {t('skipLabel')}
            </Button>
          </Modal.Footer>
        </>
      )}

      {step === 'organization' && (
        <OrganizationStep onDone={handleOrganizationDone} onSkip={handleSkip} />
      )}

      {step === 'accountType' && (
        <AccountTypeStep
          deciding={accountType.phase === 'deciding'}
          declaring={accountType.declaring}
          refused={accountType.refused}
          onCompany={continueAfterSetup}
          onFirm={() => void accountType.declare()}
          onSkip={handleSkip}
        />
      )}

      {step === 'preview' && (
        <>
          <Modal.Header onClose={handleSkip}>
            <span className='text-lg font-medium'>{t('preview.header')}</span>
          </Modal.Header>

          <Modal.Content className='flex flex-col items-center gap-md p-lg sm:p-xl text-center'>
            <p className='text-sm text-foreground-muted'>{t('preview.subtitle')}</p>

            {/* PDF preview — blob URL avoids Content-Disposition: attachment in production */}
            <div className='w-full h-112 rounded-lg border border-border-base overflow-hidden bg-background-muted'>
              {previewBlobUrl ? (
                <object
                  data={`${previewBlobUrl}#toolbar=0&navpanes=0`}
                  type='application/pdf'
                  className='w-full h-full'
                  aria-label='Sample invoice preview'
                >
                  <div className='flex items-center justify-center h-full'>
                    <Icon name='file-lines' size='xl' className='text-foreground-muted' />
                  </div>
                </object>
              ) : (
                <div className='flex items-center justify-center h-full'>
                  <Icon name='file-lines' size='xl' className='text-foreground-muted' />
                </div>
              )}
            </div>
          </Modal.Content>

          <Modal.Footer>
            <Modal.Action>
              <Button
                variant='primary'
                size='md'
                onClick={() => setStep('processing')}
                className='w-full bg-black! hover:bg-foreground-base! text-white!'
              >
                <Icon name='bolt' className='mr-xs' />
                {t('preview.proceed')}
              </Button>
            </Modal.Action>
          </Modal.Footer>
        </>
      )}

      {step === 'processing' && (
        <>
          <Modal.Header onClose={handleSkip}>
            <span className='text-lg font-medium'>{t('steps.extraction')}</span>
          </Modal.Header>

          <Modal.Content className='flex flex-col items-center gap-lg p-lg sm:p-xl text-center'>
            <div className='animate-pulse'>
              <TappeerAvatarE className='w-32 h-32' />
            </div>

            <div className='flex flex-col gap-sm'>
              <h2 className='text-xl font-semibold text-foreground-base'>
                {t('processing.title')}
              </h2>
              <p className='text-sm text-foreground-muted'>{t('processing.subtitle')}</p>
            </div>

            {/* Progress animation */}
            <div className='w-full max-w-xs'>
              <div className='h-2 rounded-full bg-background-muted overflow-hidden'>
                <div className='h-full bg-primary-base rounded-full animate-[onboarding-progress_8s_ease-in-out_forwards]' />
              </div>
              <p className='text-xs text-foreground-muted mt-sm'>{t('processing.hint')}</p>
            </div>
          </Modal.Content>
        </>
      )}

      {step === 'complete' && (
        <>
          <Modal.Header onClose={handleSkip}>
            <span className='text-lg font-medium'>{t('steps.done')}</span>
          </Modal.Header>

          <Modal.Content className='flex flex-col items-center gap-lg p-lg sm:p-xl text-center'>
            <TappeerAvatarE className='w-24 h-24' />

            <div className='flex flex-col items-center gap-sm'>
              <h2 className='text-xl font-semibold text-foreground-base'>{t('complete.title')}</h2>
              <Loader size='sm' />
              <p className='text-sm text-foreground-muted'>{t('complete.subtitle')}</p>
              <p className='text-xs text-foreground-muted'>{t('complete.dataSafety')}</p>
            </div>
          </Modal.Content>
        </>
      )}
    </Modal>
  );
}
